import { useState } from "react";
import { useSelector } from "react-redux";
import { useGestock } from "../GestockContext";
import {
  useCommandes,
  useTerminalStocks,
  useStations,
  useLivraisonsOrphelines,
  useFournisseurs,
} from "../useOilmed";
import {
  creerCommande,
  modifierCommande,
  supprimerCommande,
  supprimerLivraisonOrpheline,
  rattacherLivraisonOrpheline,
  commandeExiste,
  besoinLPCommande,
  isPastDateOnly,
  ORPHELINES_LP_DEPUIS,
} from "../oilmed.service";
import { fmt, showToast } from "../utils";
import { isWithinRange } from "../monthUtils";
import FraisMiseEnPlace from "./FraisMiseEnPlace";

// Livraisons orphelines antérieures à cette date sont ignorées ici — elles
// pré-datent le suivi "Commande introuvable" dans cette liste (même seuil que
// la déduction LP, cf. oilmed.service).
const ORPHELINES_DEPUIS = ORPHELINES_LP_DEPUIS;

const STA_LABEL = { lumumba: "LUMUMBA", limete: "LIMETE", barumbu: "BARUMBU" };

const PRODUCT_BADGE = {
  essence: { icon: "💧", label: "Super", color: "var(--blue)" },
  go: { icon: "⛽", label: "Go", color: "var(--orange)" },
};

export default function GCommandes() {
  const { terminal, produit, monthRange } = useGestock();
  const { isAdmin } = useSelector((state) => state.auth);
  const [cmdProduit, setCmdProduit] = useState(produit);
  const { commandes, loading } = useCommandes(terminal, produit, monthRange);
  // Toutes les orphelines du terminal : la liste n'affiche que le produit
  // de la page, le contrôle LP du formulaire utilise le produit commandé.
  const { orphelines: orphelinesTerminal } = useLivraisonsOrphelines(terminal);
  const orphelines = orphelinesTerminal.filter((o) => o.produit === produit);
  const { stocks } = useTerminalStocks(terminal);
  const { stationsArray } = useStations(cmdProduit);
  const lpDispo = stocks?.[cmdProduit]?.lp ?? 0;
  const { liste: fournisseurs } = useFournisseurs(terminal);
  // Répartition par fournisseur démarrée pour le produit commandé ?
  const cmdRepartition = stocks?.[cmdProduit]?.repartition ?? null;

  // Livraisons sans commande correspondante → affichées dans la liste comme
  // des pseudo-commandes avec statut "introuvable", au même titre que les
  // vraies commandes (en_attente / partiel / cloture).
  const orphelinesAsCommandes = orphelines
    .filter((o) => (o.createdAt?.toDate?.() ?? new Date(0)) >= ORPHELINES_DEPUIS)
    .filter((o) => isWithinRange(o.createdAt, monthRange))
    .map((o) => ({
      id: o.id,
      isOrpheline: true,
      stationId: o.station,
      fournisseur: o.fournisseur || null,
      produit: o.produit,
      terminal: o.terminal,
      station: STA_LABEL[o.station] ?? (o.station || "").toUpperCase(),
      numBL: o.numBL,
      qtyCom: o.volumeM3 || 0,
      totalLivre: o.volumeM3 || 0,
      livraisons: [{ id: o.id, date: o.createdAt, volumeM3: o.volumeM3 }],
      statut: "introuvable",
      date: o.createdAt,
      createdAt: o.createdAt,
    }));

  function parseQtyInput(value) {
    const raw = String(value || "").trim();
    if (!raw) return 0;
    const compact = raw.replace(/[\s  ]/g, "");
    const normalized = compact.includes(",")
      ? compact.replace(/\./g, "").replace(/,/g, ".")
      : compact;
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");

  // ── Formulaire commande ──
  const [numProgramme, setNumProgramme] = useState("");
  const [cmdFournisseur, setCmdFournisseur] = useState("");
  // Livraison sans commande à l'origine du formulaire (« Créer la commande »)
  const [orphelineSource, setOrphelineSource] = useState(null);
  const [cmdDate, setCmdDate] = useState(() =>
    new Date().toISOString().slice(0, 10),
  );
  // Selected stations (ordered array of ids)
  const [selectedStations, setSelectedStations] = useState([]);
  // Per-station: array of { qty, bl } rows
  const [staFields, setStaFields] = useState({});

  const toggleStation = (id) => {
    if (selectedStations.includes(id)) {
      setSelectedStations((prev) => prev.filter((s) => s !== id));
      setStaFields((f) => {
        const copy = { ...f };
        delete copy[id];
        return copy;
      });
    } else {
      setSelectedStations((prev) => [...prev, id]);
      setStaFields((f) => ({ ...f, [id]: [{ qty: "", bl: "" }] }));
    }
  };

  const setRowField = (stationId, rowIdx, key, val) =>
    setStaFields((prev) => {
      const rows = [...(prev[stationId] || [])];
      rows[rowIdx] = { ...rows[rowIdx], [key]: val };
      return { ...prev, [stationId]: rows };
    });

  const addRow = (stationId) =>
    setStaFields((prev) => ({
      ...prev,
      [stationId]: [...(prev[stationId] || []), { qty: "", bl: "" }],
    }));

  const removeRow = (stationId, rowIdx) =>
    setStaFields((prev) => {
      const rows = (prev[stationId] || []).filter((_, i) => i !== rowIdx);
      if (rows.length === 0) {
        setSelectedStations((s) => s.filter((id) => id !== stationId));
        const copy = { ...prev };
        delete copy[stationId];
        return copy;
      }
      return { ...prev, [stationId]: rows };
    });

  // All BL rows with qty > 0 across all stations
  const allRows = selectedStations.flatMap((id) =>
    (staFields[id] || []).map((row, idx) => ({
      stationId: id,
      rowIdx: idx,
      ...row,
    })),
  );
  const activeRows = allRows.filter((r) => parseQtyInput(r.qty) > 0);
  const totalCmd = allRows.reduce((a, r) => a + parseQtyInput(r.qty), 0);
  // Part de la commande pas encore déduite du LP (une livraison déjà arrivée
  // sans commande, même BL + station, est déjà déduite).
  const besoinLP = besoinLPCommande(
    activeRows.map((r) => ({
      stationId: r.stationId,
      qty: parseQtyInput(r.qty),
      bl: r.bl,
    })),
    orphelinesTerminal.filter((o) => o.produit === cmdProduit),
  );
  // Commande datée dans le passé = régularisation → pas de blocage LP
  const lpDepassé = !isPastDateOnly(cmdDate) && besoinLP > lpDispo + 1e-6;
  // Soldes d'ouverture saisis → fournisseur obligatoire et LP fournisseur contrôlé
  const fournisseurRequis = !!cmdRepartition?.demarree;
  const lpFournisseur =
    cmdRepartition && cmdFournisseur
      ? cmdRepartition.parFournisseur[cmdFournisseur] ?? 0
      : null;
  const lpFournisseurDepasse =
    fournisseurRequis &&
    lpFournisseur !== null &&
    !isPastDateOnly(cmdDate) &&
    besoinLP > lpFournisseur + 1e-6;

  const blNumbers = activeRows.map((r) => r.bl.trim()).filter(Boolean);
  const hasDuplicateBL = blNumbers.length > new Set(blNumbers).size;
  // BL déjà utilisé par une commande chargée (même terminal + produit).
  // Contrôle indicatif : la liste est limitée au mois/produit affiché, le
  // contrôle complet est refait dans creerCommande.
  const blsExistants = new Set(
    cmdProduit === produit
      ? commandes
          .filter((c) => c.terminal === terminal && c.produit === cmdProduit)
          // Depuis une livraison sans commande, la commande existante de la
          // même station peut être celle à laquelle on la rattache.
          .filter(
            (c) =>
              !orphelineSource ||
              c.station !== orphelineSource.stationId?.toUpperCase(),
          )
          .map((c) => String(c.numBL || "").trim())
      : [],
  );
  const hasExistingBL = blNumbers.some((b) => blsExistants.has(b));
  const rowsMissingBL = activeRows.filter((r) => !r.bl.trim());

  // Une commande ne peut pas dépasser le libre pratique disponible
  // (contrôle refait dans creerCommande).
  const canSubmit =
    activeRows.length > 0 &&
    rowsMissingBL.length === 0 &&
    numProgramme.trim() !== "" &&
    (!fournisseurRequis || !!cmdFournisseur) &&
    !hasDuplicateBL &&
    !hasExistingBL &&
    !lpDepassé &&
    !lpFournisseurDepasse;

  function resetForm() {
    setStaFields({});
    setSelectedStations([]);
    setNumProgramme("");
    setCmdFournisseur("");
    setOrphelineSource(null);
    setCmdDate(new Date().toISOString().slice(0, 10));
  }

  async function handleCreerBL() {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Pass all rows so multiple BLs per station are preserved
      const rows = activeRows.map((r) => ({
        stationId: r.stationId,
        qty: parseQtyInput(r.qty),
        bl: r.bl.trim(),
      }));

      // Depuis une livraison sans commande : si la commande (programme + BL)
      // existe déjà pour cette station, on y rattache la livraison au lieu
      // de créer une commande (ex. BL mal saisi par la station).
      if (orphelineSource && rows.length === 1 && rows[0].stationId === orphelineSource.stationId) {
        const cmdId = `${rows[0].stationId.toUpperCase()}_${numProgramme.trim()}_${rows[0].bl}`;
        if (await commandeExiste(cmdId)) {
          await rattacherLivraisonOrpheline({
            terminalId: orphelineSource.terminal,
            orphelineId: orphelineSource.id,
            commandeId: cmdId,
          });
          showToast(`Livraison BL ${orphelineSource.numBL} rattachée à ${cmdId} ✔`);
          setShowForm(false);
          resetForm();
          return;
        }
      }

      const ids = await creerCommande({
        terminalId: terminal,
        produit: cmdProduit,
        numProgramme: numProgramme.trim(),
        rows,
        date: cmdDate,
        fournisseur: cmdFournisseur || null,
      });
      showToast(`${ids.length} commande(s) créée(s) ✔`);
      setShowForm(false);
      resetForm();
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSubmitting(false);
    }
  }

  // ── Édition commande en attente ──
  const [editingCmd, setEditingCmd] = useState(null);
  const [editStation, setEditStation] = useState("");
  const [editQty, setEditQty] = useState("");
  const [editFournisseur, setEditFournisseur] = useState("");
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [confirmSupprCmd, setConfirmSupprCmd] = useState(false);

  function openEdit(cmd) {
    setEditingCmd(cmd);
    setConfirmSupprCmd(false);
    setEditStation(cmd.station?.toLowerCase() || "");
    setEditQty(String(cmd.qtyCom || ""));
    setEditFournisseur(cmd.fournisseur || "");
  }
  // Commande partiellement livrée : seul le fournisseur est modifiable
  const editPartiel = editingCmd?.statut === "partiel";

  const editQtyParsed = parseQtyInput(editQty);
  // Hausse de quantité limitée au libre pratique disponible (contrôle refait
  // dans modifierCommande ; les commandes historiques sont exemptées)
  const editHausse = editingCmd
    ? editQtyParsed - Number(editingCmd.qtyCom || 0)
    : 0;
  const editLpDispo = editingCmd
    ? stocks?.[editingCmd.produit]?.lp ?? 0
    : 0;
  const editRepartition = editingCmd
    ? stocks?.[editingCmd.produit]?.repartition ?? null
    : null;
  const editLpFournisseur =
    editRepartition?.demarree && editFournisseur
      ? editRepartition.parFournisseur[editFournisseur] ?? 0
      : null;
  const editLpDepasse =
    !!editingCmd &&
    !editingCmd.isHistorical &&
    editHausse > 0 &&
    (editHausse > editLpDispo + 1e-6 ||
      (editLpFournisseur !== null && editHausse > editLpFournisseur + 1e-6));
  const canSaveEdit =
    editStation && editQtyParsed > 0 && !editSubmitting && !editLpDepasse;

  async function handleModifier() {
    if (!canSaveEdit || !editingCmd) return;
    setEditSubmitting(true);
    try {
      await modifierCommande(editingCmd, {
        newStation: editStation,
        newQtyCom: editQtyParsed,
        newFournisseur: editFournisseur,
      });
      showToast("Commande modifiée ✔");
      setEditingCmd(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setEditSubmitting(false);
    }
  }

  // Admin : suppression d'une commande pas encore livrée
  const canSupprimerCmd =
    isAdmin &&
    editingCmd?.statut === "en_attente" &&
    Number(editingCmd?.totalLivre || 0) === 0;

  async function handleSupprimerCommande() {
    if (!canSupprimerCmd) return;
    setEditSubmitting(true);
    try {
      await supprimerCommande(editingCmd);
      showToast(`Commande ${editingCmd.id} supprimée ✔`);
      setEditingCmd(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setEditSubmitting(false);
    }
  }

  // ── Régularisation d'une livraison sans commande ──
  const [orphelineCmd, setOrphelineCmd] = useState(null);
  const [confirmSuppr, setConfirmSuppr] = useState(false);
  const [supprSubmitting, setSupprSubmitting] = useState(false);

  function openOrpheline(cmd) {
    setOrphelineCmd(cmd);
    setConfirmSuppr(false);
  }

  // Ouvre le formulaire de commande pré-rempli (station, N° BL, quantité,
  // date de livraison) : il ne reste qu'à saisir le numéro de programme.
  // À la création, commandeCreatedBackfill rattache la livraison.
  function creerCommandeDepuisOrpheline(cmd) {
    const d = cmd.date?.toDate?.() ?? new Date();
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    setCmdProduit(cmd.produit);
    resetForm();
    setCmdDate(iso);
    setSelectedStations([cmd.stationId]);
    setCmdFournisseur(cmd.fournisseur || "");
    setOrphelineSource(cmd);
    setStaFields({
      [cmd.stationId]: [{ qty: String(cmd.qtyCom || ""), bl: cmd.numBL || "" }],
    });
    setOrphelineCmd(null);
    setShowForm(true);
  }

  async function handleSupprimerOrpheline() {
    if (!orphelineCmd) return;
    setSupprSubmitting(true);
    try {
      await supprimerLivraisonOrpheline({
        terminalId: orphelineCmd.terminal,
        orphelineId: orphelineCmd.id,
      });
      showToast(`Livraison supprimée — ${fmt(orphelineCmd.qtyCom)} m³ restitués au LP ✔`);
      setOrphelineCmd(null);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSupprSubmitting(false);
    }
  }

  const STATUS_ORDER = { introuvable: 0, en_attente: 1, partiel: 2, cloture: 3 };

  const dateMs = (c) => c.date?.toMillis?.() ?? c.createdAt?.toMillis?.() ?? 0;

  const filteredCommandes = [...commandes, ...orphelinesAsCommandes].sort(
    (a, b) => {
      // Tri principal : par date (programme), plus récent en premier
      const dateDiff = dateMs(b) - dateMs(a);
      if (dateDiff !== 0) return dateDiff;
      // À date égale : statut (introuvable → en attente → partiel → clôturé)
      const statusDiff =
        (STATUS_ORDER[a.statut] ?? 1) - (STATUS_ORDER[b.statut] ?? 1);
      if (statusDiff !== 0) return statusDiff;
      return (
        (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0)
      );
    },
  );

  const finalCommandes =
    statusFilter === "all"
      ? filteredCommandes
      : filteredCommandes.filter((cmd) => cmd.statut === statusFilter);

  if (loading) {
    return <div className="empty">Chargement…</div>;
  }

  return (
    <div className="fade-up">
      <div className="page-header">
        <div className="page-title">
          Commandes <span>&amp; Livraisons</span>
        </div>
        <div className="page-sub">
          {"// "}
          BONS DE LIVRAISON — LIVRAISONS PARTIELLES POSSIBLES
        </div>
      </div>

      <div className="alert alert-blue">
        📌 Le libre pratique est déduit{" "}
        <strong>uniquement à la livraison</strong>, pas à la création du BL.
      </div>

      {/* Status Filter Tabs */}
      <div
        style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}
      >
        {[
          { key: "all", label: "Toutes", count: filteredCommandes.length },
          {
            key: "introuvable",
            label: "❓ Introuvable",
            count: filteredCommandes.filter((c) => c.statut === "introuvable")
              .length,
          },
          {
            key: "en_attente",
            label: "⏳ En attente",
            count: filteredCommandes.filter((c) => c.statut === "en_attente")
              .length,
          },
          {
            key: "partiel",
            label: "⟳ Partiel",
            count: filteredCommandes.filter((c) => c.statut === "partiel")
              .length,
          },
          {
            key: "cloture",
            label: "✔ Clôturé",
            count: filteredCommandes.filter((c) => c.statut === "cloture")
              .length,
          },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setStatusFilter(tab.key)}
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border:
                statusFilter === tab.key
                  ? "2px solid var(--blue)"
                  : "1px solid var(--border)",
              background:
                statusFilter === tab.key
                  ? "var(--blue)15"
                  : "var(--bg-elevated)",
              color: statusFilter === tab.key ? "var(--blue)" : "var(--text)",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: statusFilter === tab.key ? 600 : 400,
              transition: "all 0.2s",
            }}
          >
            {tab.label} ({tab.count})
          </button>
        ))}
      </div>

      <button
        className="btn btn-green btn-full"
        style={{ marginBottom: 14 }}
        onClick={() => {
          setCmdProduit(produit);
          resetForm();
          setShowForm(true);
        }}
      >
        ＋ Passer commande
      </button>

      <FraisMiseEnPlace terminal={terminal} produit={produit} commandes={commandes} />

      {finalCommandes.length === 0 ? (
        <div className="empty">
          {statusFilter === "all"
            ? `Aucune commande pour ${terminal}`
            : `Aucune commande ${
                statusFilter === "en_attente"
                  ? "en attente"
                  : statusFilter === "partiel"
                    ? "partielle"
                    : statusFilter === "introuvable"
                      ? "introuvable"
                      : "clôturée"
              }`}
        </div>
      ) : (
        finalCommandes.map((cmd) => {
          const productBadge =
            PRODUCT_BADGE[cmd.produit] ?? PRODUCT_BADGE.essence;
          const reste = (cmd.qtyCom || 0) - (cmd.totalLivre || 0);
          const pct =
            cmd.qtyCom > 0
              ? Math.round(((cmd.totalLivre || 0) / cmd.qtyCom) * 100)
              : 0;
          const statutBadge =
            cmd.statut === "introuvable" ? (
              <span className="badge badge-red">❓ Commande introuvable</span>
            ) : cmd.statut === "cloture" ? (
              <span className="badge badge-green">✔ Clôturé</span>
            ) : cmd.statut === "partiel" ? (
              <span className="badge badge-blue">⟳ Partiel</span>
            ) : (
              <span className="badge badge-gold">⏳ En attente</span>
            );

          return (
            <div key={cmd.id} className="bl-card" style={{ marginBottom: 14 }}>
              <div className="bl-head">
                <div>
                  <div className="bl-num">
                    {cmd.isOrpheline ? `BL ${cmd.numBL || "—"}` : cmd.id}
                  </div>
                  <div className="bl-meta" style={{ marginTop: 4 }}>
                    {cmd.date?.toDate?.()?.toLocaleDateString("fr-FR") ?? "—"}
                  </div>
                  {cmd.numProg && (
                    <div
                      className="bl-meta"
                      style={{
                        marginTop: 2,
                        color: "var(--blue)",
                        fontWeight: 600,
                        fontFamily: "var(--font-mono)",
                        fontSize: 10,
                      }}
                    >
                      Programme: {cmd.numProg}
                    </div>
                  )}
                  {cmd.station && (
                    <div
                      className="bl-meta"
                      style={{
                        marginTop: 2,
                        color: "var(--text)",
                        fontSize: 10,
                      }}
                    >
                      Station: {cmd.station}
                    </div>
                  )}
                  {cmd.fournisseur && (
                    <div
                      className="bl-meta"
                      style={{ marginTop: 2, color: "var(--text)", fontSize: 10 }}
                    >
                      Fournisseur: {cmd.fournisseur}
                    </div>
                  )}
                </div>
                <div className="bl-badges">
                  {(cmd.statut === "en_attente" ||
                    cmd.statut === "partiel" ||
                    cmd.isOrpheline) && (
                    <button
                      onClick={() =>
                        cmd.isOrpheline ? openOrpheline(cmd) : openEdit(cmd)
                      }
                      title="Modifier"
                      style={{
                        background: "transparent",
                        border: "1px solid var(--border)",
                        borderRadius: 6,
                        color: "var(--muted)",
                        cursor: "pointer",
                        fontSize: 13,
                        padding: "4px 8px",
                        lineHeight: 1,
                      }}
                    >
                      ✏
                    </button>
                  )}
                  <span
                    className="badge"
                    style={{
                      background: `${productBadge.color}20`,
                      color: productBadge.color,
                      border: `1px solid ${productBadge.color}40`,
                    }}
                  >
                    {productBadge.icon} {productBadge.label}
                  </span>
                  <span
                    className="badge"
                    style={{
                      background: "var(--blue)20",
                      color: "var(--blue)",
                      border: "1px solid var(--blue)40",
                    }}
                  >
                    🏭 {cmd.terminal}
                  </span>
                  {statutBadge}
                </div>
              </div>

              {cmd.isOrpheline ? (
                <div
                  style={{
                    padding: "8px 14px",
                    borderBottom: "1px solid var(--border2)",
                  }}
                >
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      color: "var(--muted)",
                      marginBottom: 6,
                    }}
                  >
                    Quantité livrée:{" "}
                    <strong style={{ color: "var(--green)" }}>
                      {fmt(cmd.totalLivre)} m³
                    </strong>
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: "var(--red)",
                      background: "var(--red-dim)",
                      border: "1px solid rgba(224,30,90,0.25)",
                      borderRadius: 6,
                      padding: "6px 10px",
                    }}
                  >
                    ⚠ Livraison comptabilisée (LP déduit) mais aucune commande
                    ne correspond à ce N° BL — à régulariser.
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    padding: "8px 14px",
                    borderBottom: "1px solid var(--border2)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      gap: 14,
                      fontFamily: "var(--font-mono)",
                      fontSize: 10,
                      color: "var(--muted)",
                      flexWrap: "wrap",
                      marginBottom: 6,
                    }}
                  >
                    <span>
                      Commandé:{" "}
                      <strong style={{ color: "var(--white)" }}>
                        {fmt(cmd.qtyCom)} m³
                      </strong>
                    </span>
                    <span>
                      Livré:{" "}
                      <strong style={{ color: "var(--green)" }}>
                        {fmt(cmd.totalLivre)} m³
                      </strong>
                    </span>
                    <span>
                      Reste:{" "}
                      <strong
                        style={{
                          color: reste > 0 ? "var(--gold)" : "var(--green)",
                        }}
                      >
                        {fmt(reste)} m³
                      </strong>
                    </span>
                  </div>
                  <div className="prog-wrap">
                    <div
                      className="prog-fill"
                      style={{
                        width: `${pct}%`,
                        background:
                          pct >= 100
                            ? "var(--green)"
                            : pct > 0
                              ? "var(--blue)"
                              : "var(--muted)",
                      }}
                    />
                  </div>
                </div>
              )}

              {cmd.livraisons.length > 0 && (
                <div style={{ padding: "12px 14px" }}>
                  <div
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 8,
                      color: "var(--muted)",
                      letterSpacing: 1,
                      textTransform: "uppercase",
                      marginBottom: 8,
                    }}
                  >
                    Livraisons ({cmd.livraisons.length})
                  </div>
                  {cmd.livraisons.map((liv, idx) => (
                    <div
                      key={liv.id || idx}
                      className="bl-liv-row"
                      style={{
                        padding: "6px 0",
                        borderBottom: "1px solid var(--border)",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 11, color: "var(--text)" }}>
                          {liv.date?.toDate?.()?.toLocaleDateString("fr-FR") ??
                            "—"}
                        </div>
                      </div>
                      <div
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 12,
                          fontWeight: 600,
                          color: "var(--green)",
                        }}
                      >
                        +{fmt(liv.volumeM3)} m³
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      )}

      {/* ── Modal Nouvelle Commande ── */}
      <div
        className={`modal-overlay ${showForm ? "open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setShowForm(false)}
      >
        <div className="modal">
          <div className="modal-handle" />
          <div className="modal-title">Nouvelle commande</div>
          <div className="modal-sub">{"// "}CAPACITÉ VÉRIFIÉE AVANT ENVOI</div>

          {/* Produit */}
          <div className="form-group">
            <label>Produit</label>
            <select
              value={cmdProduit}
              onChange={(e) => setCmdProduit(e.target.value)}
            >
              <option value="essence">Super</option>
              <option value="go">Go</option>
            </select>
          </div>

          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              value={cmdDate}
              onChange={(e) => setCmdDate(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>Numéro Programme</label>
            <input
              type="text"
              value={numProgramme}
              placeholder="55288"
              onChange={(e) => setNumProgramme(e.target.value)}
            />
          </div>

          {fournisseurs.length > 0 && (
            <div className="form-group">
              <label>Fournisseur{fournisseurRequis ? "" : " (optionnel)"}</label>
              <select
                value={cmdFournisseur}
                onChange={(e) => setCmdFournisseur(e.target.value)}
              >
                <option value="">
                  {fournisseurRequis ? "— Choisir —" : "Non attribué"}
                </option>
                {fournisseurs.map((f) => (
                  <option key={f} value={f}>
                    {f} — {fmt(cmdRepartition?.parFournisseur[f] ?? 0)} m³
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="info-strip">
            <span>
              Libre pratique {terminal} :{" "}
              <span className={lpDepassé ? "err" : ""}>{fmt(lpDispo)} m³</span>
            </span>
            <span>
              Total commandé :{" "}
              <span className={lpDepassé ? "err" : ""}>{fmt(totalCmd)} m³</span>
            </span>
            {besoinLP < totalCmd && (
              <span>
                Déjà livré sans commande : {fmt(totalCmd - besoinLP)} m³
              </span>
            )}
            {lpDepassé && (
              <span style={{ color: "var(--red)" }}>
                ⛔ Libre pratique insuffisant — ajoutez d'abord le LP
              </span>
            )}
            {lpFournisseur !== null && (
              <span>
                LP {cmdFournisseur} :{" "}
                <span className={lpFournisseurDepasse ? "err" : ""}>
                  {fmt(lpFournisseur)} m³
                </span>
              </span>
            )}
            {lpFournisseurDepasse && (
              <span style={{ color: "var(--red)" }}>
                ⛔ Libre pratique {cmdFournisseur} insuffisant
              </span>
            )}
          </div>

          {/* Station selector */}
          <div className="section-label">Stations</div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              marginBottom: 16,
            }}
          >
            {stationsArray.map((sta) => {
              const isSelected = selectedStations.includes(sta.id);
              return (
                <button
                  key={sta.id}
                  onClick={() => toggleStation(sta.id)}
                  style={{
                    padding: "8px 14px",
                    borderRadius: 8,
                    border: isSelected
                      ? "2px solid var(--green)"
                      : "1px solid var(--border)",
                    background: isSelected
                      ? "var(--green)15"
                      : "var(--bg-elevated)",
                    color: isSelected ? "var(--green)" : "var(--muted)",
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: isSelected ? 700 : 400,
                    fontFamily: "var(--font-mono)",
                    letterSpacing: 0.5,
                    transition: "all 0.15s",
                  }}
                >
                  🏪 {sta.label || sta.id}
                </button>
              );
            })}
          </div>

          {/* Qty + BL rows per selected station */}
          {selectedStations.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                color: "var(--muted)",
                fontSize: 12,
                padding: "12px 0",
                fontStyle: "italic",
              }}
            >
              Sélectionner au moins une station
            </div>
          ) : (
            selectedStations.map((id) => {
              const sta = stationsArray.find((s) => s.id === id);
              const rows = staFields[id] || [];
              return (
                <div key={id} className="sta-block">
                  <div className="sta-block-name">🏪 {sta?.label || id}</div>

                  {rows.map((row, rowIdx) => {
                    const q = parseQtyInput(row.qty);
                    const needBL = q > 0 && !row.bl.trim();
                    const isDuplicate =
                      q > 0 &&
                      row.bl.trim() &&
                      blNumbers.filter((b) => b === row.bl.trim()).length > 1;
                    const isExisting =
                      q > 0 && row.bl.trim() && blsExistants.has(row.bl.trim());
                    return (
                      <div key={rowIdx} style={{ marginBottom: 8 }}>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "flex-end",
                          }}
                        >
                          <div
                            className="form-group"
                            style={{ flex: 1, marginBottom: 0 }}
                          >
                            {rowIdx === 0 && <label>N° BL</label>}
                            <input
                              type="text"
                              className={needBL || isDuplicate || isExisting ? "error" : ""}
                              value={row.bl}
                              placeholder="Ex: 1656"
                              onChange={(e) =>
                                setRowField(id, rowIdx, "bl", e.target.value)
                              }
                            />
                          </div>
                          <div
                            className="form-group"
                            style={{ flex: 1, marginBottom: 0 }}
                          >
                            {rowIdx === 0 && <label>Quantité (m³)</label>}
                            <input
                              type="text"
                              inputMode="decimal"
                              value={row.qty}
                              placeholder="20 ou 20,5"
                              onChange={(e) =>
                                setRowField(id, rowIdx, "qty", e.target.value)
                              }
                            />
                          </div>
                          <button
                            onClick={() => removeRow(id, rowIdx)}
                            style={{
                              padding: "8px 10px",
                              borderRadius: 6,
                              border: "1px solid var(--border)",
                              background: "transparent",
                              color: "var(--red)",
                              cursor: "pointer",
                              fontSize: 14,
                              lineHeight: 1,
                              flexShrink: 0,
                              marginBottom: 0,
                            }}
                          >
                            ×
                          </button>
                        </div>
                        {needBL && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--gold)",
                              marginTop: 3,
                            }}
                          >
                            ⚠ N° BL requis
                          </div>
                        )}
                        {isDuplicate && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--red)",
                              marginTop: 3,
                            }}
                          >
                            ⚠ N° BL déjà utilisé !
                          </div>
                        )}
                        {isExisting && !isDuplicate && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "var(--red)",
                              marginTop: 3,
                            }}
                          >
                            ⚠ N° BL déjà utilisé par une commande {terminal}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  <button
                    onClick={() => addRow(id)}
                    style={{
                      marginTop: 4,
                      padding: "6px 12px",
                      borderRadius: 6,
                      border: "1px dashed var(--blue)",
                      background: "transparent",
                      color: "var(--blue)",
                      cursor: "pointer",
                      fontSize: 11,
                      fontFamily: "var(--font-mono)",
                      letterSpacing: 0.5,
                    }}
                  >
                    + Ajouter BL
                  </button>
                </div>
              );
            })
          )}

          <div className="btn-row">
            <button
              className="btn btn-green"
              style={{ flex: 1 }}
              disabled={!canSubmit || submitting}
              onClick={handleCreerBL}
            >
              {submitting ? "En cours…" : "✔ Passer commande"}
            </button>
            <button
              className="btn btn-ghost"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </button>
          </div>
        </div>
      </div>

      {/* ── Modal Livraison sans commande ── */}
      <div
        className={`modal-overlay ${orphelineCmd ? "open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setOrphelineCmd(null)}
      >
        <div className="modal">
          <div className="modal-handle" />
          <div className="modal-title">Commande introuvable</div>
          <div className="modal-sub">
            {"// "}LIVRAISON SANS COMMANDE — À RÉGULARISER
          </div>

          {orphelineCmd && (
            <>
              <div className="info-strip">
                <span>BL : {orphelineCmd.numBL || "—"}</span>
                <span>Station : {orphelineCmd.station || "—"}</span>
                <span>
                  Livré : {fmt(orphelineCmd.qtyCom)} m³ (
                  {PRODUCT_BADGE[orphelineCmd.produit]?.label ?? orphelineCmd.produit})
                </span>
                <span>
                  Date :{" "}
                  {orphelineCmd.date?.toDate?.()?.toLocaleDateString("fr-FR") ?? "—"}
                </span>
              </div>

              {!confirmSuppr ? (
                <>
                  <button
                    className="btn btn-green btn-full"
                    style={{ marginBottom: 10 }}
                    onClick={() => creerCommandeDepuisOrpheline(orphelineCmd)}
                  >
                    ＋ Créer la commande (ajouter le N° programme)
                  </button>
                  <div className="btn-row">
                    <button
                      className="btn btn-ghost"
                      style={{ flex: 1, color: "var(--red)" }}
                      onClick={() => setConfirmSuppr(true)}
                    >
                      🗑 Supprimer la livraison
                    </button>
                    <button
                      className="btn btn-ghost"
                      onClick={() => setOrphelineCmd(null)}
                    >
                      Annuler
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--red)",
                      background: "var(--red-dim)",
                      border: "1px solid rgba(224,30,90,0.25)",
                      borderRadius: 6,
                      padding: "8px 10px",
                      marginBottom: 12,
                    }}
                  >
                    Supprimer cette livraison ? Les{" "}
                    <strong>{fmt(orphelineCmd.qtyCom)} m³</strong> déduits seront
                    restitués au libre pratique {orphelineCmd.terminal}.
                  </div>
                  <div className="btn-row">
                    <button
                      className="btn btn-green"
                      style={{ flex: 1, background: "var(--red)" }}
                      disabled={supprSubmitting}
                      onClick={handleSupprimerOrpheline}
                    >
                      {supprSubmitting ? "En cours…" : "✔ Confirmer la suppression"}
                    </button>
                    <button
                      className="btn btn-ghost"
                      onClick={() => setConfirmSuppr(false)}
                    >
                      Retour
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Modal Modifier Commande ── */}
      <div
        className={`modal-overlay ${editingCmd ? "open" : ""}`}
        onClick={(e) => e.target === e.currentTarget && setEditingCmd(null)}
      >
        <div className="modal">
          <div className="modal-handle" />
          <div className="modal-title">Modifier commande</div>
          <div className="modal-sub">
            {"// "}
            {editPartiel
              ? "PARTIELLEMENT LIVRÉE — FOURNISSEUR"
              : "EN ATTENTE — STATION, QUANTITÉ & FOURNISSEUR"}
          </div>

          {editingCmd && (
            <>
              <div
                style={{
                  fontFamily: "var(--font-mono)",
                  fontSize: 10,
                  color: "var(--muted)",
                  marginBottom: 14,
                  padding: "8px 10px",
                  background: "var(--bg-elevated)",
                  borderRadius: 6,
                  border: "1px solid var(--border)",
                }}
              >
                {editingCmd.id}
              </div>

              <div className="form-group">
                <label>Station</label>
                <select
                  value={editStation}
                  disabled={editPartiel}
                  onChange={(e) => setEditStation(e.target.value)}
                >
                  <option value="">— Choisir —</option>
                  {stationsArray.map((sta) => (
                    <option key={sta.id} value={sta.id}>
                      {sta.label || sta.id}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Quantité (m³)</label>
                <input
                  type="text"
                  inputMode="decimal"
                  value={editQty}
                  placeholder="Ex: 20"
                  disabled={editPartiel}
                  onChange={(e) => setEditQty(e.target.value)}
                />
              </div>

              {fournisseurs.length > 0 && (
                <div className="form-group">
                  <label>Fournisseur</label>
                  <select
                    value={editFournisseur}
                    onChange={(e) => setEditFournisseur(e.target.value)}
                  >
                    <option value="">Non attribué</option>
                    {fournisseurs.map((f) => (
                      <option key={f} value={f}>
                        {f}
                        {editRepartition
                          ? ` — ${fmt(editRepartition.parFournisseur[f] ?? 0)} m³`
                          : ""}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {editHausse > 0 && (
                <div className="info-strip">
                  <span>
                    Hausse : <span className={editLpDepasse ? "err" : ""}>+{fmt(editHausse)} m³</span>
                  </span>
                  <span>
                    Libre pratique {editingCmd.terminal} :{" "}
                    <span className={editLpDepasse ? "err" : ""}>{fmt(editLpDispo)} m³</span>
                  </span>
                  {editLpDepasse && (
                    <span style={{ color: "var(--red)" }}>
                      ⛔ Libre pratique insuffisant — ajoutez d'abord le LP
                    </span>
                  )}
                </div>
              )}

              <div className="btn-row">
                <button
                  className="btn btn-green"
                  style={{ flex: 1 }}
                  disabled={!canSaveEdit}
                  onClick={handleModifier}
                >
                  {editSubmitting ? "En cours…" : "✔ Enregistrer"}
                </button>
                <button
                  className="btn btn-ghost"
                  onClick={() => setEditingCmd(null)}
                >
                  Annuler
                </button>
              </div>

              {canSupprimerCmd &&
                (!confirmSupprCmd ? (
                  <button
                    className="btn btn-ghost btn-full"
                    style={{ marginTop: 10, color: "var(--red)" }}
                    onClick={() => setConfirmSupprCmd(true)}
                  >
                    🗑 Supprimer la commande
                  </button>
                ) : (
                  <>
                    <div
                      style={{
                        fontSize: 12,
                        color: "var(--red)",
                        background: "var(--red-dim)",
                        border: "1px solid rgba(224,30,90,0.25)",
                        borderRadius: 6,
                        padding: "8px 10px",
                        margin: "10px 0 12px",
                      }}
                    >
                      Supprimer la commande <strong>{editingCmd.id}</strong> (
                      {fmt(editingCmd.qtyCom)} m³) ? Aucune livraison n'a été
                      faite, rien n'est à restituer au libre pratique.
                    </div>
                    <div className="btn-row">
                      <button
                        className="btn btn-green"
                        style={{ flex: 1, background: "var(--red)" }}
                        disabled={editSubmitting}
                        onClick={handleSupprimerCommande}
                      >
                        {editSubmitting ? "En cours…" : "✔ Confirmer la suppression"}
                      </button>
                      <button
                        className="btn btn-ghost"
                        onClick={() => setConfirmSupprCmd(false)}
                      >
                        Retour
                      </button>
                    </div>
                  </>
                ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
